// Runs background removal entirely in the browser using U2Netp — an
// Apache-2.0-licensed model (commercially safe, unlike BRIA's RMBG models,
// which are non-commercial-only). The model (client/public/models/u2netp.onnx, ~4.6 MB, from the
// official rembg v0.0.0 release) and the onnxruntime WebAssembly files (client/public/ort/, copied from
// the installed onnxruntime-web version) are served from our own domain: GitHub release links send no
// CORS headers, so browsers cannot read them from another site. Cached by the browser after the first
// run; no image data ever leaves the device.

let sessionPromise: Promise<import("onnxruntime-web/wasm").InferenceSession> | null = null;

const MODEL_URL = "/models/u2netp.onnx";
const MODEL_MIN_BYTES = 4_000_000; // a missing file can come back as the app's HTML page with status 200
const MODEL_INPUT_SIZE = 320;
const MEAN = [0.485, 0.456, 0.406];
const STD = [0.229, 0.224, 0.225];
// A full-resolution phone photo (12MP+) creates a very large canvas to
// composite the final transparent PNG against -- slow, and on lower-memory
// devices, a real cause of the process failing outright rather than just
// being slow. The model itself only ever looks at a 320x320 downscaled
// version regardless, so capping the OUTPUT at a still-generous resolution
// costs no real quality for the vast majority of use cases (profile
// pictures, product photos, web graphics) while meaningfully reducing
// memory pressure and processing time for oversized inputs.
const MAX_OUTPUT_DIMENSION = 2000;

async function getSession() {
  if (!sessionPromise) {
    sessionPromise = (async () => {
      const ort = await import("onnxruntime-web/wasm");
      // The runtime files are copied from the installed onnxruntime-web, so JS and WASM versions always match.
      ort.env.wasm.wasmPaths = "/ort/";

      let lastError: unknown;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await fetch(MODEL_URL, { cache: "force-cache" });
          if (!res.ok) throw new Error(`model request returned HTTP ${res.status}`);
          const bytes = new Uint8Array(await res.arrayBuffer());
          if (bytes.byteLength < MODEL_MIN_BYTES) throw new Error(`model file is too small (${bytes.byteLength} bytes)`);
          return await ort.InferenceSession.create(bytes, { executionProviders: ["wasm"] });
        } catch (err) {
          lastError = err;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
      // Reset so a retry (e.g. after the network recovers) doesn't just
      // replay the same cached rejected promise forever.
      sessionPromise = null;
      console.error("Background removal: model failed to load after 3 attempts", lastError);
      throw new Error("MODEL_LOAD_FAILED");
    })();
  }
  return sessionPromise;
}

import { decodeImage } from "@/tools/image/decode";

async function loadImage(file: File): Promise<{ naturalWidth: number; naturalHeight: number } & CanvasImageSource> {
  const { canvas } = await decodeImage(file); // also opens HEIC / AVIF / TIFF
  return Object.assign(canvas, { naturalWidth: canvas.width, naturalHeight: canvas.height });
}

/**
 * Removes the background from `file` and returns a transparent PNG blob URL.
 * `onProgress` receives a rough 0-100 status for the UI.
 */
export async function removeBackground(
  file: File,
  onProgress?: (pct: number, label: string) => void
): Promise<string> {
  const ort = await import("onnxruntime-web/wasm");
  onProgress?.(5, "Loading model...");
  const session = await getSession();

  onProgress?.(30, "Reading image...");
  const img = await loadImage(file);

  // Preprocess: resize to the model's expected input, normalize.
  const inputCanvas = document.createElement("canvas");
  inputCanvas.width = MODEL_INPUT_SIZE;
  inputCanvas.height = MODEL_INPUT_SIZE;
  const inputCtx = inputCanvas.getContext("2d")!;
  inputCtx.drawImage(img, 0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);
  const { data } = inputCtx.getImageData(0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);

  const chw = new Float32Array(3 * MODEL_INPUT_SIZE * MODEL_INPUT_SIZE);
  const plane = MODEL_INPUT_SIZE * MODEL_INPUT_SIZE;
  for (let i = 0; i < plane; i++) {
    const r = data[i * 4] / 255;
    const g = data[i * 4 + 1] / 255;
    const b = data[i * 4 + 2] / 255;
    chw[i] = (r - MEAN[0]) / STD[0];
    chw[plane + i] = (g - MEAN[1]) / STD[1];
    chw[plane * 2 + i] = (b - MEAN[2]) / STD[2];
  }

  const inputTensor = new ort.Tensor("float32", chw, [1, 3, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE]);
  const inputName = session.inputNames[0];
  const outputName = session.outputNames[0];

  onProgress?.(60, "Removing background...");
  const results = await session.run({ [inputName]: inputTensor });
  const maskTensor = results[outputName];
  const maskData = maskTensor.data as Float32Array;

  // Normalize mask to 0-1.
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < maskData.length; i++) {
    if (maskData[i] < min) min = maskData[i];
    if (maskData[i] > max) max = maskData[i];
  }
  const range = max - min || 1;

  onProgress?.(85, "Compositing image...");

  // Paint the mask onto a small canvas, then let the browser scale it back
  // up to the original image size with bilinear smoothing.
  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = MODEL_INPUT_SIZE;
  maskCanvas.height = MODEL_INPUT_SIZE;
  const maskCtx = maskCanvas.getContext("2d")!;
  const maskImageData = maskCtx.createImageData(MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);
  for (let i = 0; i < plane; i++) {
    const alpha = Math.round(((maskData[i] - min) / range) * 255);
    maskImageData.data[i * 4] = 255;
    maskImageData.data[i * 4 + 1] = 255;
    maskImageData.data[i * 4 + 2] = 255;
    maskImageData.data[i * 4 + 3] = alpha;
  }
  maskCtx.putImageData(maskImageData, 0, 0);

  const outCanvas = document.createElement("canvas");
  const longestSide = Math.max(img.naturalWidth, img.naturalHeight);
  const outputScale = longestSide > MAX_OUTPUT_DIMENSION ? MAX_OUTPUT_DIMENSION / longestSide : 1;
  outCanvas.width = Math.round(img.naturalWidth * outputScale);
  outCanvas.height = Math.round(img.naturalHeight * outputScale);
  const outCtx = outCanvas.getContext("2d")!;
  outCtx.drawImage(img, 0, 0, outCanvas.width, outCanvas.height);
  outCtx.globalCompositeOperation = "destination-in";
  outCtx.drawImage(maskCanvas, 0, 0, outCanvas.width, outCanvas.height);

  onProgress?.(100, "Done");

  return new Promise(resolve => {
    outCanvas.toBlob(blob => {
      resolve(URL.createObjectURL(blob!));
    }, "image/png");
  });
}
